import React, { useEffect, useMemo, useState } from "react";

type SkillCategory =
  | "All"
  | "Study Skills"
  | "Exam Skills"
  | "Digital Skills"
  | "Career Skills";

type SkillTrack = {
  id: number;
  title: string;
  seoTitle: string;
  category: Exclude<SkillCategory, "All">;
  tag: string;
  duration: string;
  level: string;
  description: string;
  image: string;
  imageAlt: string;
  modules: string[];
  keywords: string;
};

type Highlight = {
  value: string;
  label: string;
  keywords: string;
};

type ToolkitItem = {
  title: string;
  desc: string;
  keywords: string;
};

type Outcome = {
  title: string;
  desc: string;
  keywords: string;
};

const categories: SkillCategory[] = [
  "All",
  "Study Skills",
  "Exam Skills",
  "Digital Skills",
  "Career Skills",
];

const categorySeoText: Record<SkillCategory, string> = {
  All: "Skill development programs for students covering study habits, revision planning, exam strategy, digital learning, LMS usage, communication and confidence building.",
  "Study Skills":
    "Study skills programs for students focused on daily study planning, smart revision, short notes, time management and consistent learning habits.",
  "Exam Skills":
    "Exam skills programs for board exam, JEE and NEET students focused on answer writing, MCQ solving, test strategy, accuracy improvement and performance analysis.",
  "Digital Skills":
    "Digital learning skills for students to use LMS dashboard, live classes, recorded lectures, online notes, quizzes and progress tracking effectively.",
  "Career Skills":
    "Career and confidence skills for students including communication, class participation, doubt asking, presentation practice and personality development.",
};

const skillTracks: SkillTrack[] = [
  {
    id: 1,
    title: "Smart Study and Revision Skills",
    seoTitle:
      "Smart Study Skills Course for Students with Revision Planning and Short Notes",
    category: "Study Skills",
    tag: "Academic Growth",
    duration: "4 Weeks",
    level: "Class 7 to 12",
    description:
      "A practical skill track that helps students plan daily studies, revise chapters effectively, prepare short notes and build a consistent learning routine for school exams, JEE and NEET preparation.",
    image:
      "https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Student improving smart study skills with revision planning short notes and daily learning routine",
    modules: [
      "Daily study planning",
      "Revision techniques",
      "Short notes preparation",
      "Time management habits",
    ],
    keywords:
      "smart study skills, revision skills for students, study planning, short notes preparation, daily study routine, time management for students, Class 7 to 12 study skills",
  },
  {
    id: 2,
    title: "Problem Solving for JEE and NEET",
    seoTitle:
      "JEE NEET Problem Solving Skills Course for MCQs Numericals and PYQ Practice",
    category: "Exam Skills",
    tag: "Competitive Exams",
    duration: "6 Weeks",
    level: "JEE / NEET",
    description:
      "Students learn how to approach MCQs, solve numerical problems, avoid common mistakes and improve speed, accuracy and confidence for JEE Main, JEE Advanced and NEET UG competitive exams.",
    image:
      "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "JEE and NEET students practicing MCQ problem solving numericals and previous year questions",
    modules: [
      "Question approach",
      "Accuracy improvement",
      "PYQ-based practice",
      "Numerical solving method",
    ],
    keywords:
      "JEE problem solving, NEET problem solving, MCQ solving skills, JEE numericals, NEET MCQ practice, PYQ practice, exam accuracy improvement",
  },
  {
    id: 3,
    title: "Board Exam Answer Writing",
    seoTitle:
      "Board Exam Answer Writing Course for CBSE Students with Time Management",
    category: "Exam Skills",
    tag: "Board Preparation",
    duration: "3 Weeks",
    level: "Class 9 to 12",
    description:
      "This track focuses on structured answer writing, diagram presentation, time management, step marking and exam paper presentation for better CBSE board exam performance.",
    image:
      "https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "CBSE board exam student practicing answer writing time management and paper presentation",
    modules: [
      "Answer structure",
      "Paper presentation",
      "Time management",
      "Diagram and step marking",
    ],
    keywords:
      "board exam answer writing, CBSE answer writing, paper presentation skills, board exam time management, Class 10 board preparation, Class 12 board preparation",
  },
  {
    id: 4,
    title: "Digital Learning and LMS Usage",
    seoTitle:
      "Digital Learning Skills Course for LMS Dashboard Live Classes Notes and Quizzes",
    category: "Digital Skills",
    tag: "Online Learning",
    duration: "2 Weeks",
    level: "All Students",
    description:
      "Students learn how to use live classes, recorded lectures, online notes, quizzes, assignments, performance dashboard and LMS tools in a productive and organized way.",
    image:
      "https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Student using LMS dashboard live classes online notes quizzes and digital learning tools",
    modules: [
      "Using LMS dashboard",
      "Online quiz practice",
      "Tracking progress",
      "Recorded class revision",
    ],
    keywords:
      "digital learning skills, LMS usage, student dashboard, online learning tools, live class dashboard, recorded lectures, online quiz practice, progress tracking",
  },
  {
    id: 5,
    title: "Communication and Confidence",
    seoTitle:
      "Communication and Confidence Building Course for Students and Class Participation",
    category: "Career Skills",
    tag: "Personality Growth",
    duration: "4 Weeks",
    level: "Foundation",
    description:
      "A confidence-building track where students practice speaking clearly, asking doubts, presenting ideas, participating in class and becoming more active learners.",
    image:
      "https://images.unsplash.com/photo-1523580846011-d3a5bc25702b?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Student building communication skills confidence doubt asking habit and class participation",
    modules: [
      "Speaking practice",
      "Doubt asking habit",
      "Class participation",
      "Idea presentation",
    ],
    keywords:
      "communication skills for students, confidence building, doubt asking habit, class participation, personality development for students, speaking practice",
  },
  {
    id: 6,
    title: "Test Strategy and Performance Analysis",
    seoTitle:
      "Test Strategy Course for Students with Performance Analysis and Score Improvement",
    category: "Exam Skills",
    tag: "Performance",
    duration: "5 Weeks",
    level: "Exam Focused",
    description:
      "Students learn how to analyze test results, identify weak chapters, improve speed, reduce negative marking and create a better strategy for upcoming board, JEE and NEET exams.",
    image:
      "https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=900&h=650&fit=crop&auto=format&q=80",
    imageAlt:
      "Student analyzing test performance score report weak topics and exam strategy",
    modules: [
      "Test analysis",
      "Weak topic tracking",
      "Score improvement plan",
      "Speed and accuracy review",
    ],
    keywords:
      "test strategy for students, performance analysis, score improvement, weak topic tracking, exam strategy, mock test analysis, JEE NEET test planning",
  },
];

const highlights: Highlight[] = [
  {
    value: "Skill-first",
    label: "Learning Approach",
    keywords:
      "skill first learning approach, student skill development, practical learning habits",
  },
  {
    value: "Live + Recorded",
    label: "Flexible Classes",
    keywords:
      "live classes, recorded classes, flexible online learning, LMS-based learning",
  },
  {
    value: "Practice",
    label: "Worksheets & Quizzes",
    keywords:
      "worksheets, online quizzes, practice sessions, student learning practice",
  },
];

const toolkit: ToolkitItem[] = [
  {
    title: "Skill Roadmaps",
    desc: "Clear learning paths for academic skills, exam skills, digital learning and confidence-building skills.",
    keywords:
      "student skill roadmap, academic roadmap, exam skills roadmap, learning path for students",
  },
  {
    title: "Guided Practice",
    desc: "Worksheets, quizzes, revision tasks and practice sessions help students build stronger daily habits.",
    keywords:
      "guided practice, worksheets, student quizzes, revision tasks, daily study habits",
  },
  {
    title: "Mentor Feedback",
    desc: "Faculty feedback helps students understand mistakes, improve learning methods and grow continuously.",
    keywords:
      "mentor feedback, teacher feedback, mistake analysis, student improvement, academic guidance",
  },
  {
    title: "Progress Dashboard",
    desc: "Students can track classes, practice work, test scores, revision activity and skill improvement.",
    keywords:
      "progress dashboard, student performance tracking, test score tracking, revision tracking, LMS dashboard",
  },
];

const outcomes: Outcome[] = [
  {
    title: "Better Study Discipline",
    desc: "Students learn how to follow a regular routine and study with consistency.",
    keywords: "study discipline, daily routine, consistent learning habits",
  },
  {
    title: "Improved Exam Confidence",
    desc: "Students become more confident through practice, revision and test strategy.",
    keywords: "exam confidence, board exam confidence, JEE NEET confidence",
  },
  {
    title: "Stronger Problem-Solving Ability",
    desc: "Students learn better question approach, MCQ solving and numerical practice.",
    keywords: "problem solving skills, MCQ solving, numerical practice",
  },
  {
    title: "More Active Class Participation",
    desc: "Students become comfortable asking doubts, discussing answers and presenting ideas.",
    keywords: "class participation, doubt asking, student confidence",
  },
  {
    title: "Effective Revision Habits",
    desc: "Students revise chapters through short notes, formula sheets and planned practice.",
    keywords: "revision habits, short notes, formula sheets, planned practice",
  },
  {
    title: "Smarter Use of Online Learning Tools",
    desc: "Students learn to use live classes, notes, quizzes and dashboards effectively.",
    keywords: "online learning tools, LMS skills, digital learning for students",
  },
];

const seoKeywords = [
  "Mediit Skills",
  "LMS skills for students",
  "student skill development",
  "study skills for students",
  "smart study skills",
  "revision skills",
  "exam skills",
  "board exam answer writing",
  "JEE problem solving skills",
  "NEET problem solving skills",
  "digital learning skills",
  "LMS dashboard training",
  "online learning skills",
  "communication skills for students",
  "confidence building for students",
  "test strategy",
  "performance analysis",
  "score improvement",
  "time management for students",
  "Class 7 study skills",
  "Class 8 study skills",
  "Class 9 exam skills",
  "Class 10 board skills",
  "Class 11 JEE NEET skills",
  "Class 12 exam strategy",
  "mentor feedback",
  "guided practice",
].join(", ");

const MediitSkills: React.FC = () => {
  const [activeCategory, setActiveCategory] = useState<SkillCategory>("All");

  const filteredTracks = useMemo(() => {
    if (activeCategory === "All") return skillTracks;
    return skillTracks.filter((track) => track.category === activeCategory);
  }, [activeCategory]);

  useEffect(() => {
    const title =
      activeCategory === "All"
        ? "Mediit Skills | Study Skills, Exam Skills and Digital Learning for Students"
        : `${activeCategory} for Students | Mediit Skills - Mediit Academy`;

    const description =
      activeCategory === "All"
        ? "Mediit Skills helps students build smart study habits, revision techniques, exam strategy, digital learning confidence, communication skills, test analysis and performance improvement through LMS-based training."
        : categorySeoText[activeCategory];

    const keywords = [
      seoKeywords,
      categorySeoText[activeCategory],
      `${activeCategory} for students`,
      `${activeCategory} online course`,
      `${activeCategory} training`,
    ].join(", ");

    document.title = title;

    const setMetaTag = (name: string, content: string) => {
      let tag = document.querySelector(
        `meta[name="${name}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    const setPropertyTag = (property: string, content: string) => {
      let tag = document.querySelector(
        `meta[property="${property}"]`
      ) as HTMLMetaElement | null;

      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("property", property);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", content);
    };

    setMetaTag("description", description);
    setMetaTag("keywords", keywords);
    setMetaTag("robots", "index, follow");
    setMetaTag("author", "Mediit Academy");

    setPropertyTag("og:title", title);
    setPropertyTag("og:description", description);
    setPropertyTag("og:type", "website");
    setPropertyTag("og:site_name", "Mediit Academy");

    setMetaTag("twitter:card", "summary_large_image");
    setMetaTag("twitter:title", title);
    setMetaTag("twitter:description", description);

    let canonical = document.querySelector(
      `link[rel="canonical"]`
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }

    canonical.setAttribute("href", `${window.location.origin}/lms-skills`);
  }, [activeCategory]);

  const skillsSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Mediit Skills",
    description:
      "Skill development page for students covering study skills, exam skills, digital learning, communication, confidence building, test strategy and performance analysis.",
    url: "/lms-skills",
    keywords: seoKeywords,
    mainEntity: {
      "@type": "ItemList",
      name: "Mediit Skills Tracks",
      itemListElement: skillTracks.map((track, index) => ({
        "@type": "ListItem",
        position: index + 1,
        item: {
          "@type": "Course",
          name: track.seoTitle,
          description: track.description,
          educationalLevel: track.level,
          timeRequired: track.duration,
          courseMode: "online",
          provider: {
            "@type": "EducationalOrganization",
            name: "Mediit Academy",
            url: "/",
          },
          keywords: track.keywords,
        },
      })),
    },
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How Students Build Better Learning Skills with Mediit Skills",
    description:
      "A simple process where students choose a skill track, attend learning sessions, practice tasks, get mentor feedback and track improvement.",
    step: [
      {
        "@type": "HowToStep",
        name: "Choose a Skill Track",
        text: "Select a study skill, exam skill, digital skill or career skill according to the student's learning goal.",
      },
      {
        "@type": "HowToStep",
        name: "Attend Guided Sessions",
        text: "Learn practical techniques through live or recorded sessions inside the LMS learning system.",
      },
      {
        "@type": "HowToStep",
        name: "Practice with Worksheets and Quizzes",
        text: "Complete worksheets, quizzes, revision tasks and practice activities to build strong habits.",
      },
      {
        "@type": "HowToStep",
        name: "Track Progress and Improve",
        text: "Use mentor feedback and dashboard reports to improve study discipline, confidence and exam performance.",
      },
    ],
  };

  const goToLogin = () => {
    window.location.href = "/login";
  };

  const scrollToTracks = () => {
    const section = document.getElementById("skill-tracks");
    section?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#f7f9fc] font-['Inter'] text-[#101827]">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(skillsSchema),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(howToSchema),
        }}
      />

      <main>
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-[#f7f9fc]"
          aria-labelledby="mediit-skills-heading"
        >
          <div className="absolute left-0 top-0 h-[360px] w-[360px] rounded-full bg-blue-100/80 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-amber-100/80 blur-3xl" />

          <div className="relative mx-auto max-w-[1380px] px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-4 py-2 shadow-sm">
                  <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-black text-white">
                    M
                  </span>
                  <p className="text-xs font-black uppercase tracking-[0.18em] text-blue-600">
                    Mediit Skills
                  </p>
                </div>

                <h1
                  id="mediit-skills-heading"
                  className="mt-6 max-w-3xl text-4xl font-black leading-tight tracking-[-0.05em] text-slate-950 sm:text-5xl lg:text-[62px]"
                >
                  Practical Skills That Help Students Learn Better, Not Just
                  Study Harder
                </h1>

                <p className="mt-5 max-w-2xl text-base leading-7 text-slate-600 sm:text-lg">
                  Mediit Skills helps students build strong study habits, exam
                  strategy, digital learning confidence, communication skills and
                  performance improvement through structured LMS-based training,
                  guided practice and mentor feedback.
                </p>

                <p className="sr-only">{seoKeywords}</p>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Start Mediit Skills learning program"
                    className="rounded-2xl bg-[#101827] px-7 py-4 text-sm font-black text-white shadow-xl shadow-slate-900/15 transition-all hover:-translate-y-0.5 hover:bg-slate-800"
                  >
                    Start Skill Learning
                  </button>

                  <button
                    type="button"
                    onClick={scrollToTracks}
                    aria-label="Explore Mediit Skills tracks"
                    className="rounded-2xl border border-slate-200 bg-white px-7 py-4 text-sm font-black text-slate-950 shadow-sm transition-all hover:-translate-y-0.5 hover:border-slate-400"
                  >
                    Explore Tracks
                  </button>
                </div>

                <div
                  className="mt-9 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3"
                  aria-label="Mediit Skills highlights"
                >
                  {highlights.map((item) => (
                    <article
                      key={item.label}
                      title={item.keywords}
                      className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm"
                    >
                      <p className="text-xl font-black text-slate-950">
                        {item.value}
                      </p>
                      <p className="mt-1 text-xs font-bold text-slate-500">
                        {item.label}
                      </p>
                      <p className="sr-only">{item.keywords}</p>
                    </article>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-6">
                <div className="relative mx-auto max-w-[560px]">
                  <div className="absolute -left-3 top-10 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Weekly Practice
                    </p>
                    <p className="mt-1 text-2xl font-black text-blue-600">
                      18 Tasks
                    </p>
                  </div>

                  <div className="absolute -right-3 bottom-16 z-10 hidden rounded-3xl border border-slate-100 bg-white p-4 shadow-xl md:block">
                    <p className="text-xs font-bold text-slate-500">
                      Skill Score
                    </p>
                    <p className="mt-1 text-2xl font-black text-green-600">
                      Improving
                    </p>
                  </div>

                  <div className="rounded-[34px] border border-white bg-white p-5 shadow-[0_30px_90px_rgba(15,23,42,0.12)]">
                    <div className="rounded-[28px] bg-slate-950 p-5 text-white">
                      <div className="flex items-center justify-between gap-4">
                        <div>
                          <p className="text-xs font-black uppercase tracking-[0.16em] text-blue-200">
                            Skill Dashboard
                          </p>
                          <h2 className="mt-2 text-2xl font-black">
                            Today&apos;s Learning Plan
                          </h2>
                        </div>

                        <div className="rounded-2xl bg-white/10 px-4 py-3 text-center">
                          <p className="text-xl font-black">84%</p>
                          <p className="text-[11px] text-white/60">
                            Progress
                          </p>
                        </div>
                      </div>

                      <div className="mt-6 space-y-3">
                        <div className="rounded-2xl bg-white p-4 text-slate-950">
                          <div className="flex items-center justify-between gap-4">
                            <div>
                              <p className="text-sm font-black">
                                Smart Revision Practice
                              </p>
                              <p className="mt-1 text-xs font-medium text-slate-500">
                                25 questions · 35 minutes
                              </p>
                            </div>
                            <span className="rounded-full bg-green-50 px-3 py-1 text-xs font-black text-green-600">
                              Active
                            </span>
                          </div>
                        </div>

                        <div className="rounded-2xl bg-white/10 p-4">
                          <div className="mb-3 flex items-center justify-between">
                            <p className="text-sm font-black">
                              Weekly Skill Growth
                            </p>
                            <p className="text-xs text-white/60">5/7 days</p>
                          </div>
                          <div className="h-3 overflow-hidden rounded-full bg-white/10">
                            <div className="h-full w-[72%] rounded-full bg-blue-400" />
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="rounded-2xl bg-white/10 p-4">
                            <p className="text-2xl font-black">12</p>
                            <p className="mt-1 text-xs text-white/60">
                              Quizzes Done
                            </p>
                          </div>

                          <div className="rounded-2xl bg-white/10 p-4">
                            <p className="text-2xl font-black">4</p>
                            <p className="mt-1 text-xs text-white/60">
                              Skills Active
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-3 gap-3">
                      <div className="rounded-2xl bg-blue-50 p-4 text-center">
                        <p className="text-lg font-black text-blue-600">
                          Plan
                        </p>
                        <p className="text-xs font-bold text-slate-500">
                          Study
                        </p>
                      </div>

                      <div className="rounded-2xl bg-green-50 p-4 text-center">
                        <p className="text-lg font-black text-green-600">
                          Learn
                        </p>
                        <p className="text-xs font-bold text-slate-500">
                          Skills
                        </p>
                      </div>

                      <div className="rounded-2xl bg-orange-50 p-4 text-center">
                        <p className="text-lg font-black text-orange-600">
                          Track
                        </p>
                        <p className="text-xs font-bold text-slate-500">
                          Growth
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TOOLKIT */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-9 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-6">
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Learning Toolkit
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Everything Students Need to Build Strong Learning Habits
                </h2>
              </div>

              <p className="text-sm leading-6 text-slate-500 lg:col-span-6">
                Mediit Skills focuses on daily improvement. Students get
                structured tasks, mentor guidance, practice material and
                performance tracking so their learning becomes measurable and
                consistent.
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {toolkit.map((item, index) => (
                <article
                  key={item.title}
                  title={item.keywords}
                  className="rounded-[28px] border border-slate-100 bg-[#f8fbff] p-6 transition-all hover:-translate-y-1 hover:bg-white hover:shadow-xl"
                >
                  <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-lg font-black text-blue-600 shadow-sm">
                    {String(index + 1).padStart(2, "0")}
                  </div>

                  <h3 className="text-xl font-black text-slate-950">
                    {item.title}
                  </h3>

                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    {item.desc}
                  </p>

                  <p className="sr-only">{item.keywords}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* SKILL TRACKS */}
        <section id="skill-tracks" className="bg-[#f7f9fc] py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="mb-7 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-600">
                  Skill Tracks
                </p>
                <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] text-slate-950 sm:text-4xl">
                  Choose the Right Skill Path for Every Student
                </h2>
              </div>

              <p className="max-w-xl text-sm leading-6 text-slate-500">
                These tracks are practical, student-focused and connected with
                academic performance, exam preparation, digital learning and
                confidence building.
              </p>
            </div>

            <div
              className="mb-8 flex gap-3 overflow-x-auto pb-2"
              aria-label="Skill category filters"
            >
              {categories.map((category) => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  title={categorySeoText[category]}
                  aria-label={`Show ${category}. ${categorySeoText[category]}`}
                  className={`shrink-0 rounded-full border px-5 py-2.5 text-sm font-black transition-all ${
                    activeCategory === category
                      ? "border-[#101827] bg-[#101827] text-white shadow-lg shadow-slate-900/15"
                      : "border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <p className="mb-6 text-sm leading-6 text-slate-500">
              {categorySeoText[activeCategory]}
            </p>

            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
              {filteredTracks.map((track) => (
                <article
                  key={track.id}
                  title={track.keywords}
                  className="group overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_22px_60px_rgba(15,23,42,0.12)]"
                >
                  <div className="relative h-[215px] overflow-hidden">
                    <img
                      src={track.image}
                      alt={track.imageAlt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/75 via-slate-950/10 to-transparent" />

                    <span className="absolute left-4 top-4 rounded-full bg-white px-3 py-1 text-xs font-black text-blue-600 shadow-sm">
                      {track.category}
                    </span>

                    <span className="absolute right-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-black text-slate-950 backdrop-blur">
                      {track.tag}
                    </span>

                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="text-2xl font-black leading-tight tracking-[-0.03em] text-white">
                        {track.title}
                      </h3>
                    </div>
                  </div>

                  <div className="p-5">
                    <p className="text-sm font-bold text-blue-700">
                      {track.seoTitle}
                    </p>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                      {track.description}
                    </p>

                    <p className="sr-only">{track.keywords}</p>

                    <div className="mt-5 grid grid-cols-2 gap-3">
                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-500">
                          Duration
                        </p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {track.duration}
                        </p>
                      </div>

                      <div className="rounded-2xl bg-slate-50 p-4">
                        <p className="text-xs font-bold text-slate-500">
                          Level
                        </p>
                        <p className="mt-1 text-sm font-black text-slate-950">
                          {track.level}
                        </p>
                      </div>
                    </div>

                    <div className="mt-5 space-y-2">
                      {track.modules.map((module) => (
                        <div key={module} className="flex items-center gap-2">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-green-50 text-xs font-black text-green-600">
                            ✓
                          </span>
                          <p className="text-sm font-medium text-slate-700">
                            {module}
                          </p>
                        </div>
                      ))}
                    </div>

                    <button
                      type="button"
                      onClick={goToLogin}
                      aria-label={`View ${track.seoTitle}`}
                      className="mt-6 w-full rounded-2xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all hover:-translate-y-0.5 hover:bg-slate-800"
                    >
                      View Skill Track
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* OUTCOMES */}
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 gap-9 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-5">
                <div className="rounded-[34px] bg-[#101827] p-7 text-white shadow-2xl lg:p-8">
                  <p className="text-sm font-black uppercase tracking-[0.18em] text-blue-200">
                    Student Outcomes
                  </p>

                  <h2 className="mt-3 text-3xl font-black tracking-[-0.04em] sm:text-4xl">
                    Skills That Support Real Academic Improvement
                  </h2>

                  <p className="mt-4 text-sm leading-7 text-white/70">
                    The purpose of Mediit Skills is to make students more
                    organized, confident and exam-ready through small but
                    consistent improvements.
                  </p>

                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Join Mediit Skills program"
                    className="mt-7 rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5"
                  >
                    Join Mediit Skills
                  </button>
                </div>
              </div>

              <div className="lg:col-span-7">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {outcomes.map((outcome) => (
                    <article
                      key={outcome.title}
                      title={outcome.keywords}
                      className="flex items-start gap-4 rounded-[24px] border border-slate-100 bg-[#f8fbff] p-5"
                    >
                      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white text-lg font-black text-blue-600 shadow-sm">
                        ✓
                      </span>
                      <div>
                        <h3 className="text-sm font-black text-slate-800">
                          {outcome.title}
                        </h3>
                        <p className="mt-2 text-xs leading-5 text-slate-500">
                          {outcome.desc}
                        </p>
                        <p className="sr-only">{outcome.keywords}</p>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#f7f9fc] py-12">
          <div className="mx-auto max-w-[1380px] px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-[36px] bg-gradient-to-r from-slate-950 via-slate-900 to-blue-950 p-8 text-white shadow-2xl lg:p-10">
              <div className="absolute -right-10 -top-10 h-44 w-44 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="absolute -bottom-10 left-10 h-44 w-44 rounded-full bg-orange-500/20 blur-3xl" />

              <div className="relative flex flex-col gap-7 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-black uppercase tracking-[0.16em] text-blue-200">
                    Build Better Learning Habits
                  </p>

                  <h2 className="mt-2 text-3xl font-black tracking-[-0.035em] sm:text-4xl">
                    Help Students Learn with Clarity, Practice and Confidence
                  </h2>

                  <p className="mt-3 max-w-2xl text-sm leading-6 text-white/70">
                    Mediit Skills connects live learning, guided practice,
                    revision, performance tracking and mentor support in one LMS
                    experience.
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={goToLogin}
                    aria-label="Login to Mediit Skills dashboard"
                    className="rounded-2xl bg-white px-7 py-4 text-sm font-black text-slate-950 transition-all hover:-translate-y-0.5"
                  >
                    Login to Dashboard
                  </button>

                  <button
                    type="button"
                    onClick={scrollToTracks}
                    aria-label="Explore all Mediit Skills tracks"
                    className="rounded-2xl border border-white/15 bg-white/10 px-7 py-4 text-sm font-black text-white backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white/15"
                  >
                    Explore Skills
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default MediitSkills;